import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, IconButton, Paper, TextField, Typography, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { useGetInstallerByPinQuery } from "../../Api/attoDeskApi";
import {
  NAVY, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../theme/tokens";

const formatBytes = (b?: number | null): string => {
  if (!b) return "—";
  if (b < 1_048_576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1_048_576).toFixed(1)} MB`;
};

const InstallerPage: React.FC = () => {
  const { pin: urlPin } = useParams<{ pin?: string }>();
  const navigate = useNavigate();
  const [pinInput, setPinInput] = useState("");

  const activePin = urlPin || "";
  const { data, isFetching, isError, error } = useGetInstallerByPinQuery(activePin, {
    skip: !activePin,
  });

  const submit = () => {
    const p = pinInput.trim().toUpperCase();
    if (p.length >= 4) navigate(`/installer/${p}`);
  };

  const copy = (text: string) => navigator.clipboard.writeText(text);

  if (!activePin) {
    return (
      <Box sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        display: "flex", alignItems: "center", justifyContent: "center", p: 4,
      }}>
        <Paper elevation={0} sx={{
          p: 5, borderRadius: 4, maxWidth: 480, width: "100%",
          background: "#fff", boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
        }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: 2,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.18)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
              color: "#7c3aed", mb: 2,
            }}>
              <InstallMobileIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY, letterSpacing: "-0.02em" }}>
              VM Display — Installer
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mt: 1 }}>
              Enter the PIN your VM Display contact gave you to view the
              setup info for every screen on this account.
            </Typography>
          </Box>
          <TextField fullWidth size="medium" placeholder="ABCD-1234" value={pinInput}
            onChange={(e) => setPinInput(e.target.value.toUpperCase())}
            inputProps={{ style: { textAlign: "center", fontFamily: "monospace", fontSize: 24, letterSpacing: "0.2em" } }}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            sx={{ mb: 2 }} />
          <Button fullWidth variant="contained" disableElevation onClick={submit}
            disabled={pinInput.trim().length < 4}
            sx={{
              textTransform: "none", fontWeight: 700, py: 1.5,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              color: "#0b1e2d",
            }}>
            Unlock
          </Button>
        </Paper>
      </Box>
    );
  }

  if (isFetching) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", color: "#fff", bgcolor: "#0f172a" }}>
        Loading…
      </Box>
    );
  }

  if (isError || !data?.status) {
    const msg = (error as any)?.data?.message || "PIN invalid or expired";
    return (
      <Box sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        display: "flex", alignItems: "center", justifyContent: "center", p: 4,
      }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, maxWidth: 420, width: "100%", textAlign: "center" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#b91c1c", mb: 1 }}>
            {msg}
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 3 }}>
            Ask your VM Display contact to issue a fresh PIN.
          </Typography>
          <Button variant="outlined" onClick={() => navigate("/installer")}
            sx={{ textTransform: "none" }}>
            Try another PIN
          </Button>
        </Paper>
      </Box>
    );
  }

  const d = (data?.data as any) || {};
  const devices: any[] = d.devices || [];
  const release = d.android_release;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f1f5f9", p: 3 }}>
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            color: "#fff",
          }}>
            <InstallMobileIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY }}>
              {d.company?.name || "VM Display"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
              Installer setup info · {devices.length} device{devices.length === 1 ? "" : "s"}
              {d.pin_expires_at && (
                <span style={{ marginLeft: 12 }}>· PIN expires {new Date(d.pin_expires_at).toLocaleDateString()}</span>
              )}
            </Typography>
          </Box>
        </Box>

        {/* Android Release card */}
        {release && (
          <Paper elevation={0} sx={{
            p: 3, mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`,
            background: `linear-gradient(135deg, ${alpha("#3DDC84", 0.06)} 0%, transparent 100%)`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <DownloadIcon sx={{ color: "#3DDC84", fontSize: 28 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>
                  Android Player APK
                </Typography>
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                  v{release.version_name} (code {release.version_code}) · {formatBytes(release.file_size)}
                </Typography>
                {release.release_notes && (
                  <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 0.5 }}>
                    {release.release_notes}
                  </Typography>
                )}
              </Box>
              <Button variant="contained" disableElevation
                startIcon={<DownloadIcon />}
                href={release.download_url}
                sx={{
                  textTransform: "none", fontWeight: 700, borderRadius: 2,
                  bgcolor: "#3DDC84", color: "#0b1e2d",
                  "&:hover": { bgcolor: "#2eb96d" },
                }}>
                Download APK
              </Button>
            </Box>
          </Paper>
        )}

        {/* Devices */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY }}>
              Devices to set up
            </Typography>
            <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
              Use the device code on the Android setup screen, or open the Web Player URL in a TV browser.
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Code", "Name", "Location", "Web Player URL", "Short URL", ""].map((h) => (
                    <TableCell key={h}
                      sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                        textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: "center", py: 5, color: TEXT_SECONDARY }}>
                      No devices configured yet.
                    </TableCell>
                  </TableRow>
                ) : devices.map((dev: any) => (
                  <TableRow key={dev.device_code}>
                    <TableCell>
                      <Chip size="small" label={dev.device_code}
                        sx={{ height: 24, fontWeight: 800, fontFamily: "monospace",
                          bgcolor: alpha("#7c3aed", 0.1), color: "#7c3aed" }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                      {dev.device_name}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                      {dev.location || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: TEXT_SECONDARY, fontFamily: "monospace", maxWidth: 240 }}>
                      <Typography noWrap sx={{ fontSize: 11, fontFamily: "monospace" }}>
                        {dev.web_player_url}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontFamily: "monospace", color: TEXT_PRIMARY }}>
                      {dev.short_url}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Copy short URL">
                        <IconButton size="small" onClick={() => copy(dev.short_url)}>
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open in this browser">
                        <IconButton size="small"
                          onClick={() => window.open(dev.web_player_url, "_blank")}>
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Quick instructions */}
        <Paper elevation={0} sx={{ p: 3, mt: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, mb: 2 }}>
            Quick install steps
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY, mb: 1 }}>
            <strong>Android TV / T95 / Fire TV:</strong> Download the APK above. Install. Open the player. Enter the device code shown in the table.
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY, mb: 1 }}>
            <strong>LG / Samsung TV browser:</strong> Open the Short URL on the TV. Set as homepage.
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY }}>
            <strong>API base URL:</strong> <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>{d.api_base_url}</code>
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default InstallerPage;
